<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1><i class="fa fa-rocket"></i> {{ $t('deployTitle') }}</h1>
        <p class="mb-0 text-muted">{{ $t('deployHint') }}</p>
      </div>
      <ul class="app-breadcrumb breadcrumb">
        <li class="breadcrumb-item"><i class="fa fa-home fa-lg"></i></li>
        <li class="breadcrumb-item">{{ $t('breadcrumbTools') }}</li>
        <li class="breadcrumb-item">{{ $t('deployTitle') }}</li>
      </ul>
    </div>

    <div class="d-flex justify-content-end mb-2">
      <button class="btn btn-sm btn-light" :disabled="loading" @click="loadRepos">
        <b-spinner v-if="loading" small></b-spinner>
        <i v-else class="fa fa-sync"></i> {{ $t('refresh') }}
      </button>
    </div>

    <div v-if="loading && !repos.length" class="text-center py-5">
      <b-spinner></b-spinner>
    </div>

    <div v-for="r in repos" :key="r.key" class="card mb-3">
      <div class="card-body py-3">
        <div class="d-flex align-items-center flex-wrap">
          <div class="mr-3">
            <div class="h6 mb-0">{{ r.name }}</div>
            <div class="small text-muted">
              <code>{{ r.devBranch }}</code> <i class="fa fa-arrow-right"></i> <code>{{ r.prodBranch }}</code>
              · {{ r.project }}
            </div>
          </div>

          <div class="mr-3">
            <b-badge v-if="r.error" variant="danger" :title="r.error">{{ $t('loadError') }}</b-badge>
            <b-badge v-else-if="r.ahead > 0" variant="warning">{{ $t('aheadBy', [r.ahead]) }}</b-badge>
            <b-badge v-else variant="success">{{ $t('upToDate') }}</b-badge>
            <b-link v-if="r.ahead > 0" class="small ml-2" @click="toggle(r.key)">
              {{ expanded[r.key] ? $t('hideCommits') : $t('showCommits') }}
            </b-link>
          </div>

          <div class="ml-auto">
            <button v-if="canDeploy"
                    class="btn btn-sm btn-primary"
                    :disabled="r.ahead === 0 || !!r.error || promoting[r.key] || isActive(r.key)"
                    @click="confirmPromote(r)">
              <b-spinner v-if="promoting[r.key]" small></b-spinner>
              <i v-else class="fa fa-code-branch"></i>
              {{ $t('mergeBtn') }}
            </button>
            <span v-else class="text-muted small">{{ $t('noPermission') }}</span>
          </div>
        </div>

        <!-- danh sách commit sẽ lên prod -->
        <div v-if="expanded[r.key] && r.commits.length" class="mt-3 border-top pt-2">
          <div v-for="c in r.commits" :key="c.shortId" class="small py-1 border-bottom">
            <code>{{ c.shortId }}</code>
            <span class="ml-2">{{ c.title }}</span>
            <span class="text-muted ml-2">— {{ c.author }}</span>
          </div>
          <div v-if="r.ahead > r.commits.length" class="small text-muted mt-1">
            {{ $t('andMore', [r.ahead - r.commits.length]) }}
          </div>
        </div>

        <!-- trạng thái pipeline khi đang deploy (inline, không dùng string-template component) -->
        <div v-if="deployments[r.key]" class="mt-3 border-top pt-3">
          <template v-if="dep(r.key)">
            <div v-if="dep(r.key).error" class="alert alert-danger py-2 mb-2 small">{{ dep(r.key).error }}</div>
            <div class="d-flex align-items-center flex-wrap mb-2">
              <b-badge :variant="statusVariant(pStatus(r.key))" class="mr-2">{{ pStatus(r.key) }}</b-badge>
              <a v-if="dep(r.key).mrUrl" :href="dep(r.key).mrUrl" target="_blank" class="small mr-2">MR<i class="fa fa-external-link-alt ml-1"></i></a>
              <a v-if="pipe(r.key).webUrl" :href="pipe(r.key).webUrl" target="_blank" class="small mr-2">Pipeline<i class="fa fa-external-link-alt ml-1"></i></a>
              <span v-if="pipe(r.key).elapsedSec != null" class="small text-muted mr-2">{{ $t('elapsed') }} {{ fmtDur(pipe(r.key).elapsedSec) }}</span>
              <span v-if="pipe(r.key).etaRemainingSec != null" class="small text-muted mr-2">· {{ $t('remaining') }} ~{{ fmtDur(pipe(r.key).etaRemainingSec) }}</span>
              <span v-else-if="pipe(r.key).typicalSec" class="small text-muted mr-2">· {{ $t('typical') }} ~{{ fmtDur(pipe(r.key).typicalSec) }}</span>
            </div>
            <div v-if="pipe(r.key).jobs && pipe(r.key).jobs.length" class="d-flex flex-wrap">
              <b-badge v-for="j in pipe(r.key).jobs" :key="j.name" :variant="statusVariant(j.status)" class="mr-1 mb-1" :title="j.stage">
                {{ j.name }}
              </b-badge>
            </div>
            <div v-if="pStatus(r.key) === 'success' && r.cdn" class="alert alert-info py-2 mt-2 mb-0 small">
              <i class="fa fa-clock"></i> {{ $t('cdnNote') }}
            </div>
            <div v-else-if="pStatus(r.key) === 'success'" class="alert alert-success py-2 mt-2 mb-0 small">
              <i class="fa fa-check"></i> {{ $t('deployOk') }}
            </div>
            <div v-else-if="pStatus(r.key) === 'failed'" class="alert alert-danger py-2 mt-2 mb-0 small">
              <i class="fa fa-times"></i> {{ $t('deployFailed') }}
            </div>
          </template>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
export default {
  name: 'DeployProduction',
  data() {
    return {
      loading: false,
      repos: [],
      expanded: {},
      promoting: {},
      deployments: {}, // key -> { sha, mrUrl, cdn, status, pipeline, done, error, attempts }
      pollTimer: null,
      pollNotFoundMax: 30, // ~2 phút: chưa thấy pipeline → coi như CI không chạy
      pollMax: 300,        // ~20 phút: trần theo dõi, tránh poll vô hạn
    };
  },
  computed: {
    canDeploy() {
      return ['owner', 'admin', 'tech'].includes(this.$store.state.user.role);
    },
  },
  mounted() {
    this.loadRepos();
  },
  beforeDestroy() {
    if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
  },
  methods: {
    // helper truy cập an toàn cho template
    dep(key) { return this.deployments[key] || null; },
    pipe(key) { const d = this.deployments[key]; return (d && d.pipeline) || {}; },
    pStatus(key) { const d = this.deployments[key]; if (!d) return ''; return (d.pipeline && d.pipeline.status) || d.status || 'creating'; },
    statusVariant(s) {
      switch (s) {
        case 'success': return 'success';
        case 'failed': return 'danger';
        case 'running': return 'primary';
        case 'canceled':
        case 'skipped': return 'secondary';
        case 'pending':
        case 'created':
        case 'creating': return 'warning';
        default: return 'secondary';
      }
    },
    fmtDur(sec) {
      const s = Math.max(0, Math.round(sec || 0));
      if (s < 60) return s + 's';
      const m = Math.floor(s / 60);
      const rem = s % 60;
      return rem ? `${m}m${rem}s` : `${m}m`;
    },
    async loadRepos() {
      this.loading = true;
      try {
        const res = await this.$service.get('/deploy/repos');
        this.repos = (res.data && res.data.repos) || [];
      } catch (e) {
        this.toastErr(this.$t('loadError'));
      } finally {
        this.loading = false;
      }
    },
    toggle(key) {
      this.$set(this.expanded, key, !this.expanded[key]);
    },
    isActive(key) {
      const d = this.deployments[key];
      return !!d && !d.done;
    },
    escapeHtml(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, m => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m]
      ));
    },
    confirmPromote(r) {
      // commit title do dev tự nhập → escape trước khi nhét vào html của swal (chống XSS)
      const list = r.commits.slice(0, 10).map(c => `• ${this.escapeHtml(c.shortId)} ${this.escapeHtml(c.title)}`).join('<br>');
      this.$swal({
        title: this.$t('confirmTitle', [r.name]),
        html: `${this.$t('confirmBody', [r.ahead])}<div class="text-left small mt-2">${list}</div>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('mergeBtn'),
        cancelButtonText: this.$t('cancel'),
        confirmButtonColor: '#d33',
      }).then(res => {
        if (!res.value) return;
        this.promote(r);
      });
    },
    async promote(r) {
      this.$set(this.promoting, r.key, true);
      try {
        const res = await this.$service.post('/deploy/promote', { repo: r.key });
        const d = res.data || {};
        this.$set(this.deployments, r.key, {
          sha: d.mergeSha, mrUrl: d.mrUrl, cdn: !!d.cdn,
          status: 'creating', pipeline: null, done: false, error: null, attempts: 0,
        });
        this.ensurePolling();
        this.$swal({
          title: this.$t('startedTitle'), icon: 'success', toast: true,
          position: 'top-end', timer: 2000, showConfirmButton: false,
        });
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message;
        this.toastErr(msg);
      } finally {
        this.$set(this.promoting, r.key, false);
      }
    },
    ensurePolling() {
      if (this.pollTimer) return;
      this.pollTimer = setInterval(() => this.pollAll(), 4000);
      this.pollAll(); // chạy ngay 1 lần
    },
    async pollAll() {
      const keys = Object.keys(this.deployments).filter(k => !this.deployments[k].done);
      if (!keys.length) {
        if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
        this.loadRepos(); // cập nhật lại số commit ahead sau khi deploy xong
        return;
      }
      await Promise.all(keys.map(async (key) => {
        const d = this.deployments[key];
        const attempts = (d.attempts || 0) + 1;
        try {
          const res = await this.$bgService.get('/deploy/pipeline', { params: { repo: key, sha: d.sha } });
          const data = res.data || {};
          let done = !!data.done;
          let error = null;
          if (!done) {
            // trần theo dõi: chưa thấy pipeline quá lâu / chạy quá lâu → dừng poll, báo rõ
            if (!data.found && attempts >= this.pollNotFoundMax) { done = true; error = this.$t('pipelineNotFound'); }
            else if (attempts >= this.pollMax) { done = true; error = this.$t('pollTimeout'); }
          }
          this.$set(this.deployments, key, {
            ...d, attempts,
            status: data.status || d.status,
            pipeline: data.found ? data : d.pipeline,
            done, error,
          });
        } catch (e) {
          // mạng chập chờn → giữ trạng thái cũ, chỉ tăng attempts, không dừng poll
          this.$set(this.deployments, key, { ...d, attempts });
        }
      }));
    },
    toastErr(msg) {
      this.$bvToast && this.$bvToast.toast(msg, { variant: 'danger', title: this.$t('error'), toaster: 'b-toaster-top-right' });
    },
  },
  i18n: {
    messages: {
      vi: {
        deployTitle: 'Triển khai Production',
        breadcrumbTools: 'Công cụ',
        deployHint: 'Bấm "Merge lên Production" để đưa nhánh dev lên production — CI/CD sẽ tự chạy deploy. Nút chỉ bật khi có commit mới.',
        refresh: 'Tải lại',
        aheadBy: 'Dev hơn prod {0} commit',
        upToDate: 'Đã đồng bộ',
        loadError: 'Không tải được',
        showCommits: 'Xem commit',
        hideCommits: 'Ẩn commit',
        andMore: '… và {0} commit nữa',
        mergeBtn: 'Merge lên Production',
        noPermission: 'Không có quyền',
        confirmTitle: 'Deploy production: {0}?',
        confirmBody: 'Sẽ đưa {0} commit lên production và deploy NGAY. Chắc chưa?',
        startedTitle: 'Đã bắt đầu deploy',
        cancel: 'Huỷ',
        error: 'Lỗi',
        elapsed: 'đã chạy',
        remaining: 'còn',
        typical: 'thường',
        cdnNote: 'Pipeline xong. Bản mới qua CDN (CloudFront) cần thêm vài phút để lan toàn cầu — nếu vẫn thấy bản cũ hãy đợi & hard refresh.',
        deployOk: 'Đã deploy production thành công.',
        deployFailed: 'Pipeline lỗi — mở link Pipeline để xem log.',
        pipelineNotFound: 'Không thấy pipeline sau vài phút — kiểm tra CI đã bật chưa. MR có thể đã merge, mở link MR để xem.',
        pollTimeout: 'Đã quá thời gian theo dõi. Mở link Pipeline để kiểm tra trạng thái cuối.',
      },
      en: {
        deployTitle: 'Deploy to Production',
        breadcrumbTools: 'Tools',
        deployHint: 'Click "Merge to Production" to promote the dev branch — CI/CD deploys automatically. Enabled only when there are new commits.',
        refresh: 'Reload',
        aheadBy: 'Dev is {0} commit(s) ahead',
        upToDate: 'Up to date',
        loadError: 'Load failed',
        showCommits: 'Show commits',
        hideCommits: 'Hide commits',
        andMore: '… and {0} more',
        mergeBtn: 'Merge to Production',
        noPermission: 'No permission',
        confirmTitle: 'Deploy to production: {0}?',
        confirmBody: 'This promotes {0} commit(s) to production and deploys NOW. Sure?',
        startedTitle: 'Deploy started',
        cancel: 'Cancel',
        error: 'Error',
        elapsed: 'elapsed',
        remaining: 'left',
        typical: 'typically',
        cdnNote: 'Pipeline done. The new build is served via CDN (CloudFront) and needs a few minutes to propagate — if you still see the old version, wait & hard refresh.',
        deployOk: 'Deployed to production successfully.',
        deployFailed: 'Pipeline failed — open the Pipeline link to see logs.',
        pipelineNotFound: 'No pipeline found after a few minutes — check whether CI is enabled. The MR may already be merged; open the MR link.',
        pollTimeout: 'Monitoring timed out. Open the Pipeline link to check the final status.',
      },
    },
  },
};
</script>
